import {
    defineComponent,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import {
    parsePublicMarkdown,
    type PublicMarkdownBlock,
    type PublicMarkdownInlineNode,
} from "@logscord/public-site/markdown";


//////////////////////////////////////////////////
//  MARKDOWN DOC
//////////////////////////////////////////////////


let mermaidRenderSequence = 0;
let mermaidPromise: Promise<typeof import("mermaid")["default"]> | undefined;

function loadMermaid() {
    mermaidPromise ??= import("mermaid/dist/mermaid.esm.min.mjs").then(({ default: mermaid }) => {
        mermaid.initialize({
            startOnLoad   : false,
            securityLevel : "strict",
            theme         : "dark",
            fontFamily    : '"Montserrat", Arial, sans-serif',
        });

        return mermaid;
    });

    return mermaidPromise;
}

const MermaidDiagram = defineComponent({
    props: {
        code: {
            type: String,
            required: true,
        },
    },

    setup(props) {
        const svg = ref("");
        const renderFailed = ref(false);
        let disposed = false;
        let renderVersion = 0;

        async function renderDiagram() {
            const version = ++renderVersion;
            svg.value = "";
            renderFailed.value = false;

            try {
                const [mermaid] = await Promise.all([
                    loadMermaid(),
                    document.fonts.ready,
                ]);
                const id = `public-mermaid-${++mermaidRenderSequence}`;
                const result = await mermaid.render(id, props.code);

                if (!disposed && version === renderVersion)
                    svg.value = result.svg;
            } catch {
                if (!disposed && version === renderVersion)
                    renderFailed.value = true;
            }
        }

        onMounted(() => {
            void renderDiagram();
        });
        watch(() => props.code, () => {
            if (!disposed)
                void renderDiagram();
        });
        onBeforeUnmount(() => {
            disposed = true;
            renderVersion += 1;
        });

        return () => (
            <div
                class={{
                    "public-doc-mermaid": true,
                    "public-doc-mermaid-error": renderFailed.value,
                }}
            >
                {svg.value
                    ? <div class="public-doc-mermaid-rendered" innerHTML={svg.value} />
                    : (
                        <pre>
                            <span>mermaid</span>
                            <code>{props.code}</code>
                        </pre>
                    )}
            </div>
        );
    },
});


function hasUriScheme(href: string): boolean {
    return /^[a-z][a-z\d+.-]*:/i.test(href);
}

function isExternalWebLink(href: string): boolean {
    return /^https?:\/\//i.test(href) || href.startsWith("//");
}

function normalizeDocLink(href: string): string {
    if (!hasUriScheme(href) && !href.startsWith("//") && href.endsWith(".md"))
        return `/docs/${href.replace(/\.md$/, "")}`;

    return href;
}

function renderLink(node: Extract<PublicMarkdownInlineNode, { type: "link" }>) {
    const label = renderInline(node.label);

    if (hasUriScheme(node.href) || node.href.startsWith("//") || node.href.startsWith("#")) {
        return (
            <a
                href={node.href}
                target={isExternalWebLink(node.href) ? "_blank" : undefined}
                rel={isExternalWebLink(node.href) ? "noreferrer" : undefined}
            >
                {label}
            </a>
        );
    }

    return <router-link to={node.href}>{label}</router-link>;
}

function renderInline(nodes: PublicMarkdownInlineNode[]) {
    return nodes.map((node) => {
        switch (node.type) {
            case "text":
                return node.value;

            case "strong":
                return <strong>{renderInline(node.value)}</strong>;

            case "code":
                return <code>{node.value}</code>;

            case "link":
                return renderLink(node);
        }
    });
}

function renderBlock(block: PublicMarkdownBlock) {
    switch (block.type) {
        case "heading": {
            const Tag = `h${Math.min(block.level, 4)}` as "h1" | "h2" | "h3" | "h4";
            return <Tag>{renderInline(block.content)}</Tag>;
        }

        case "paragraph":
            return <p>{renderInline(block.content)}</p>;

        case "list":
            return (
                <ul>
                    {block.items.map((item) => (
                        <li>{renderInline(item)}</li>
                    ))}
                </ul>
            );

        case "code":
            if (block.language.toLowerCase() === "mermaid")
                return <MermaidDiagram code={block.code} />;

            return (
                <pre>
                    {block.language && <span>{block.language}</span>}
                    <code>{block.code}</code>
                </pre>
            );

        case "horizontal-rule":
            return <hr />;

        case "table":
            return (
                <div class="public-doc-table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                {block.headers.map((header) => (
                                    <th>{renderInline(header)}</th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {block.rows.map((row) => (
                                <tr>
                                    {row.map((cell) => (
                                        <td>{renderInline(cell)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
    }
}

export const MarkdownDoc = defineComponent({
    props: {
        markdown: {
            type: String,
            required: true,
        },
    },

    setup(props) {
        return () => (
            <div class="public-doc-content">
                {parsePublicMarkdown(props.markdown, { normalizeLink: normalizeDocLink }).map(renderBlock)}
            </div>
        );
    },
});
